



















































































































































import { mapGetters } from 'vuex';
import { cloneDeep, isEqual } from 'lodash';
import {
  getStreamConfiguration,
  updateStreamConfiguration
} from '@/api/CopyStake/Configuration';
import {
  TStreamConfigurationPayload,
  TStreamConfigurationResponse,
  ValidationRules
} from '@/api/schema';
import { EPermissionSuperAdmin } from '@/plugins/role-permissions/types/permission-subject';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import * as vr from '@/helpers/validation';

const ConfirmationDialog = (): Promise<any> =>
  import('@/components/ConfirmationDialog.vue');

export default {
  name: 'CopyStakeStreamerSettingsForm',
  components: {
    ConfirmationDialog
  },
  data(): any {
    return {
      streamSettingsSavedData: {},

      currentCurrency: 'USD',
      betVolumes: { USD: { min: null, max: null, defaultValue: null } },
      credentials: {
        credentialsKey: '',
        credentialsSecret: '',
        streamUrl: ''
      },
      inactiveTimeout: '',
      showOnlyStreamers: false,
      operatorUrl: '',
      copystakeLobbyUrl: '',

      confirmData: {
        cancelText: 'Cancel',
        okText: `Yes, I'm sure`,
        header: 'Show only streamers',
        disclaimer: 'Are you sure you want to enable this feature?',
        okButtonType: 'primary'
      },
      isLoading: false,
      formValid: false,
      credentialsShow: {
        secretKey: true,
        apiKey: true
      },
      showConfirm: false
    };
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),
    ...mapGetters('Onboarding', [
      'isCopyStakeGeneralIntegrationMode',
      'isCopyStakeOnFlyCopyMode'
    ]),

    isShowGeneralSettings(): boolean {
      return this.isCopyStakeGeneralIntegrationMode;
    },
    isShowCurrencySettings(): boolean {
      return this.isCopyStakeOnFlyCopyMode;
    },
    isShowDefaultBetValueSettings(): boolean {
      return this.isCopyStakeOnFlyCopyMode;
    },
    isShowLiveKitSettings(): boolean {
      return (
        this.$role.can.update(
          EPermissionSuperAdmin.SUPER_ADMIN_LIVEKIT_SETTINGS
        ) && !!this.streamSettingsSavedData?.showCredentials
      );
    },
    isShowOnlyStreamersSettings(): boolean {
      return (
        this.isCopyStakeGeneralIntegrationMode &&
        this.isCopyStakeOnFlyCopyMode
      );
    },

    canEdit(): boolean {
      return this.$role.can.update('copy_stake streamers');
    },

    currencyList(): string[] {
      return Object.keys(this.betVolumes);
    },
    limitsChanged(): boolean {
      return !isEqual(
        this.betVolumes,
        this.streamSettingsSavedData.betVolumes
      );
    },
    inactivityTimeoutChanged(): boolean {
      return (
        this.inactiveTimeout !==
        this.streamSettingsSavedData?.inactiveTimeout?.streamer
      );
    },
    credentialsChanged(): boolean {
      return !isEqual(
        this.credentials,
        this.streamSettingsSavedData.credentials
      );
    },
    urlChanged(): boolean {
      return (
        (this.operatorUrl || '') !==
          (this.streamSettingsSavedData?.operatorUrl ?? '') ||
        (this.copystakeLobbyUrl ?? '') !==
          (this.streamSettingsSavedData?.copystakeLobbyUrl ?? '')
      );
    },
    valueCanBeSaved(): boolean {
      return (
        this.formValid &&
        (this.limitsChanged ||
          this.inactivityTimeoutChanged ||
          this.credentialsChanged ||
          this.urlChanged)
      );
    },
    isOptionalBetVolumesValues(): boolean {
      const currentBetVolumes = this.betVolumes[this.currentCurrency];

      return (
        this.currentCurrency !== 'USD' &&
        [
          currentBetVolumes.max,
          currentBetVolumes.min,
          currentBetVolumes.defaultValue
        ].every((v) => v === null)
      );
    },

    rules(): ValidationRules {
      const { max, min } = this.betVolumes[this.currentCurrency];

      const baseValidations = [
        (v: any) => vr.validNumber(v),
        (v: any) => vr.positiveNumber(v)
      ];

      return {
        maxBetRules:
          this.currentCurrency === 'USD'
            ? [
                (v) => v === null || vr.validNumber(v),
                (v) => v === null || vr.positiveNumber(v)
              ]
            : this.isOptionalBetVolumesValues
            ? []
            : baseValidations,

        minBetRules: this.isOptionalBetVolumesValues
          ? []
          : [
              ...baseValidations,
              (v) => !max || vr.lte(v, max, "Min Bet can't exceed Max Bet")
            ],

        defaultBetRules: this.isOptionalBetVolumesValues
          ? []
          : [
              ...baseValidations,
              (v) =>
                vr.gte(
                  v,
                  min,
                  'Default Bet Amount cannot be lower than Min Bet'
                ),
              (v) =>
                !max ||
                vr.lte(v, max, "Default Bet Amount can't exceed Max Bet")
            ],

        inactivityTimeoutRules: [
          (v) => v === '' || v === null || vr.positiveNumber(v),
          (v) => v === '' || v === null || vr.validNumber(v),
          (v) => v === '' || v === null || vr.integer(v)
        ],

        operatorUrlRules: [(v) => v === '' || v === null || vr.url(v)],
        copystakeLobbyUrlRules: [(v) => v === '' || v === null || vr.url(v)]
      };
    }
  },
  watch: {
    operatorId: {
      handler: 'getConfiguration',
      immediate: true
    }
  },
  methods: {
    onBetVolumesInput(val: string | number, betVolumesKey: string): void {
      this.betVolumes[this.currentCurrency][betVolumesKey] =
        this.parseInputValue(val);

      this.validateForm();
    },
    onInactiveTimeoutInput(val: string | number): void {
      this.inactiveTimeout = this.parseInputValue(val);

      this.validateForm();
    },
    parseInputValue(val: string | number): number | string | null {
      if (val === '') return null;

      const num = Number(val);
      return isNaN(num) ? val : num;
    },

    validateForm(): void {
      this.$nextTick(() => {
        this.$refs.form.validate();
      });
    },

    async getConfiguration(): Promise<void> {
      if (!this.operatorId) {
        return;
      }

      this.isLoading = true;

      getStreamConfiguration()
        .then((data: TStreamConfigurationResponse) => {
          this.parseSettingsData(data);
        })
        .finally(() => {
          this.credentialsShow.apiKey = false;
          this.credentialsShow.secretKey = false;
          this.isLoading = false;
        });
    },
    async updateConfiguration(changeStreamerShow = false): Promise<void> {
      this.isLoading = true;

      const payload: Partial<TStreamConfigurationPayload> = cloneDeep(
        this.streamSettingsSavedData
      );

      if (changeStreamerShow) {
        payload.showOnlyStreamers = !this.showOnlyStreamers;
      }
      if (this.limitsChanged) {
        payload.betVolumes = cloneDeep(this.betVolumes);
      }
      if (this.inactivityTimeoutChanged) {
        payload.inactiveTimeout.streamer = this.inactiveTimeout;
      }
      if (this.credentialsChanged) {
        payload.credentials = cloneDeep(this.credentials);
      }
      if (this.urlChanged) {
        payload.operatorUrl = this.operatorUrl;
        payload.copystakeLobbyUrl = this.copystakeLobbyUrl;
      }

      await updateStreamConfiguration(payload)
        .then((data: TStreamConfigurationResponse) => {
          this.parseSettingsData(data);

          this.$toast.success('Settings was successfully updated!');
        })
        .catch(errorToastMessage)
        .finally(() => {
          this.isLoading = false;
        });
    },
    parseSettingsData(data: TStreamConfigurationResponse): void {
      this.streamSettingsSavedData = cloneDeep(data);

      if (data.betVolumes) {
        this.betVolumes = cloneDeep(data.betVolumes);
      }

      this.inactiveTimeout = data.inactiveTimeout?.streamer || null;

      this.credentials.credentialsKey =
        data.credentials?.credentialsKey || null;
      this.credentials.credentialsSecret =
        data.credentials?.credentialsSecret || null;
      this.credentials.streamUrl = data.credentials?.streamUrl || null;

      this.showOnlyStreamers = data.showOnlyStreamers;

      this.operatorUrl = data.operatorUrl || null;
      this.copystakeLobbyUrl = data.copystakeLobbyUrl || null;
    },
    handleStreamerChange(): void {
      if (this.isLoading || !this.canEdit) return;

      if (!this.showOnlyStreamers) {
        this.showConfirm = true;
      } else {
        this.handleSettingsUpdate(true);
      }
    },
    handleSettingsUpdate(): void {
      this.updateConfiguration(true).finally(() => {
        this.showConfirm = false;
      });
    },
    getHiddenAttrs(key: string): any {
      const label = key === 'apiKey' ? 'API Key' : 'Secret Key';
      const appendIcon = this.credentialsShow[key]
        ? 'mdi-eye'
        : 'mdi-eye-off';
      const type = this.credentialsShow[key] ? 'text' : 'password';

      return {
        label: `LiveKit ${label}`,
        outlined: true,
        dense: true,
        appendIcon: this.canEdit ? '' : appendIcon,
        type: this.canEdit ? 'text' : type,
        autocomplete: this.canEdit ? 'off' : 'new-password'
      };
    },
    toggleVisibleStatus(key: string): void {
      this.credentialsShow[key] = !this.credentialsShow[key];
    }
  }
};
